/**
 * Forward Message Modal
 * Destination picker for forwarding messages to channels or DMs
 */

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import type { Message } from '@/types/api.types';
import { useChannels } from '@/hooks/useChannels';
import { useDMThreads } from '@/hooks/useDMs';
import { useForwardMessage } from '@/hooks/useMessages';
import { useAppStore } from '@/store/app.store';
import Avatar from '@/components/common/Avatar';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { handleTablistArrowKeys } from '@/utils/a11y';

interface ForwardMessageModalProps {
  message: Message;
  onClose: () => void;
}

type Tab = 'channels' | 'dms';

const ForwardMessageModal = ({ message, onClose }: ForwardMessageModalProps) => {
  const [activeTab, setActiveTab] = useState<Tab>('channels');
  const [search, setSearch] = useState('');
  const [comment, setComment] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [selectedDmThreadId, setSelectedDmThreadId] = useState<string | null>(null);

  const { data: channels = [] } = useChannels();
  const { data: dmThreads = [] } = useDMThreads();
  const forwardMutation = useForwardMessage();
  const { setActiveChannel, setActiveDMThread } = useAppStore();
  const { dialogRef, titleId } = useDialogA11y(onClose, { autoFocus: false });

  const filteredChannels = useMemo(() => {
    if (!search.trim()) return channels;
    const q = search.toLowerCase();
    return channels.filter((ch) => ch.name.toLowerCase().includes(q));
  }, [channels, search]);

  const filteredDMs = useMemo(() => {
    if (!search.trim()) return dmThreads;
    const q = search.toLowerCase();
    return dmThreads.filter((dm) => {
      const names = dm.members.map((m) => m.display_name?.toLowerCase() || '');
      return names.some((n) => n.includes(q));
    });
  }, [dmThreads, search]);

  const handleSelect = (type: 'channel' | 'dm', id: string) => {
    if (type === 'channel') {
      setSelectedChannelId(id);
      setSelectedDmThreadId(null);
    } else {
      setSelectedDmThreadId(id);
      setSelectedChannelId(null);
    }
  };

  const handleForward = async () => {
    if (!selectedChannelId && !selectedDmThreadId) return;

    try {
      await forwardMutation.mutateAsync({
        messageId: message.id,
        destinationChannelId: selectedChannelId || undefined,
        destinationDmThreadId: selectedDmThreadId || undefined,
        comment: comment.trim() || undefined,
      });
      const targetChannelId = selectedChannelId;
      const targetDmThreadId = selectedDmThreadId;
      toast.success('Съобщението е препратено', {
        action: {
          label: 'Отвори',
          onClick: () => {
            if (targetChannelId) {
              setActiveChannel(targetChannelId);
            } else if (targetDmThreadId) {
              setActiveDMThread(targetDmThreadId);
            }
          },
        },
      });
      onClose();
    } catch {
      toast.error('Грешка при препращане на съобщението');
    }
  };

  const getDmDisplayName = (dm: (typeof dmThreads)[number]) => {
    if (dm.other_user) return dm.other_user.display_name;
    if (dm.is_group) {
      return dm.members.map((m) => m.display_name).join(', ');
    }
    return dm.members[0]?.display_name || 'Лично съобщение';
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border-primary">
          <h3 id={titleId} className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
            Препрати съобщение
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3">
          <input
            type="text"
            placeholder="Търси..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {/* Tabs */}
        <div role="tablist" aria-label="Тип дестинация" onKeyDown={handleTablistArrowKeys} className="flex px-4 pt-3 space-x-1">
          <button
            role="tab"
            id="forward-tab-channels"
            aria-selected={activeTab === 'channels'}
            aria-controls="forward-tabpanel"
            tabIndex={activeTab === 'channels' ? 0 : -1}
            onClick={() => { setActiveTab('channels'); setSearch(''); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'channels'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-dark-text-muted dark:hover:text-dark-text-secondary'
            }`}
          >
            Канали
          </button>
          <button
            role="tab"
            id="forward-tab-dms"
            aria-selected={activeTab === 'dms'}
            aria-controls="forward-tabpanel"
            tabIndex={activeTab === 'dms' ? 0 : -1}
            onClick={() => { setActiveTab('dms'); setSearch(''); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'dms'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-dark-text-muted dark:hover:text-dark-text-secondary'
            }`}
          >
            Лични
          </button>
        </div>

        {/* List */}
        <div
          role="tabpanel"
          id="forward-tabpanel"
          aria-labelledby={activeTab === 'channels' ? 'forward-tab-channels' : 'forward-tab-dms'}
          className="px-2 py-2 max-h-60 overflow-y-auto"
        >
          {activeTab === 'channels' ? (
            filteredChannels.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400 dark:text-dark-text-muted text-center">
                Няма намерени канали
              </p>
            ) : (
              filteredChannels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => handleSelect('channel', channel.id)}
                  className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                    selectedChannelId === channel.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-500/30'
                      : 'hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary/50'
                  }`}
                >
                  <span className="text-gray-400 dark:text-dark-text-muted text-sm font-medium">#</span>
                  <span className="text-sm text-gray-900 dark:text-dark-text-primary truncate">
                    {channel.name}
                  </span>
                  {channel.type === 'private' && (
                    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                </button>
              ))
            )
          ) : filteredDMs.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-400 dark:text-dark-text-muted text-center">
              Няма намерени разговори
            </p>
          ) : (
            filteredDMs.map((dm) => (
              <button
                key={dm.id}
                onClick={() => handleSelect('dm', dm.id)}
                className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                  selectedDmThreadId === dm.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-500/30'
                    : 'hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary/50'
                }`}
              >
                <Avatar
                  user={dm.other_user || dm.members[0] || { id: '', display_name: '?', avatar_url: null }}
                  size="xs"
                />
                <span className="text-sm text-gray-900 dark:text-dark-text-primary truncate">
                  {getDmDisplayName(dm)}
                </span>
                {dm.is_group && (
                  <span className="text-[10px] text-gray-400 dark:text-dark-text-muted flex-shrink-0">
                    ({dm.members.length})
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Comment input */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-dark-border-primary">
          <textarea
            placeholder="Добави коментар (по избор)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 px-4 py-3 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text-primary rounded-lg transition-colors"
          >
            Отказ
          </button>
          <button
            onClick={handleForward}
            disabled={(!selectedChannelId && !selectedDmThreadId) || forwardMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {forwardMutation.isPending ? 'Препращане...' : 'Препрати'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardMessageModal;
